/**
 * LowCreditModal - Warning at <5 Reader Credits
 * 
 * Non-blocking modal shown when user's balance drops below 5 RC.
 * Respects a 24-hour cooldown between prompts.
 * 
 * "Not Now" closes modal and opens the credit ledger for transparency.
 */
import React, { useState } from 'react';
import { X, AlertCircle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';
import { useReaderCredits } from '@/contexts/ReaderCreditContext';
import ReaderCreditsModal from './ReaderCreditsModal';

export default function LowCreditModal() {
  const { 
    showLowCreditModal, 
    setShowLowCreditModal, 
    setShowPricingModal 
  } = useReaderFunnel();
  const { balance } = useReaderCredits();
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  
  if (!showLowCreditModal && !showLedgerModal) return null;
  
  const handleViewOptions = () => {
    setShowLowCreditModal(false);
    setShowPricingModal(true);
  };
  
  const handleDismiss = () => {
    setShowLowCreditModal(false);
    // Open the credit ledger when user clicks "Not Now"
    setShowLedgerModal(true);
  };
  
  const displayBalance = typeof balance === 'number' ? balance.toFixed(2) : '—';
  
  // If we should show the ledger modal
  if (showLedgerModal && !showLowCreditModal) {
    return (
      <ReaderCreditsModal 
        isOpen={showLedgerModal} 
        onClose={() => setShowLedgerModal(false)} 
      />
    );
  }
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleDismiss()}
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-yellow-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 mx-auto mb-3 rounded-lg"
          />
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-3 bg-yellow-600/20 rounded-xl">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">Reader Credits Are Running Low</h2>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            You have <span className="text-yellow-400 font-semibold">{displayBalance} Reader Credits</span> remaining. 
            Choose an option to keep listening and exploring.
          </p>
          
          {/* Balance indicator */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Current Balance</span>
              <span className="text-2xl font-bold text-yellow-400">{displayBalance} RC</span>
            </div>
            <div className="mt-2 bg-slate-700 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-yellow-400 h-full rounded-full transition-all duration-300"
                style={{ width: `${Math.min((balance || 0) / 10 * 100, 100)}%` }}
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleViewOptions}
              className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-semibold py-3"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              View Options
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
